import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClientService } from '@app/core/services/client.service';
import { Client } from '@app/core/models/client';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { TitleService } from '@app/core/services/title.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomValidators } from '@app/core/utils/custom-validators';
import { FeatureService } from '@app/core/services/feature.service';
import * as _ from 'lodash';
import { forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { SmsResponseLanguage } from '@app/core/models/sms-response-types';
import { SmsCustomKeyword } from '@app/core/models/sms-custom-keyword';
import { smsCustomKeywordMasterList } from '@app/core/services/sms-custom-keyword-master-list';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { LanguageEnum } from '@app/core/models/language.enum';
import { NewFeedTransitionService } from '@app/core/services/new-feed-transition.service';
import { ClientHeaderComponent } from '../../../shared/client-header/client-header.component';
import { NgClass } from '@angular/common';
import { ErrorDisplayComponent } from '../../../shared/error-display/error-display.component';
import { RadioBtnComponent } from '../../../shared/radio-btn/radio-btn.component';
import { TextInputComponent } from '../../../shared/text-input/text-input.component';
import { ClientSmsResponseComponent } from '../client-sms-response/client-sms-response.component';
import { ClientSmsCustomKeywordsComponent } from '../client-sms-custom-keywords/client-sms-custom-keywords.component';
import {
  ClientStopHelpLanguagesComponent,
  StopHelpEdits,
  StopHelpTexts,
} from '../client-stop-help-languages/client-stop-help-languages.component';
import { SmsLanguageConfigService } from '@app/core/services/sms-language-config.service';
import { Permissions, PermissionService } from '@app/core/services/permission.service';
import { ClientHelpsiteComponent } from '../client-helpsite/client-helpsite.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../../shared/message-dialog/message-dialog.component';

const STOP_ALL_KEYWORD = 'stopall';

@Component({
    selector: 'app-client-inputs-new',
    templateUrl: './client-inputs-new.component.html',
    styleUrls: ['./client-inputs-new.component.scss'],
    standalone: true,
    imports: [ClientHeaderComponent, FormsModule, ReactiveFormsModule, NgClass, ErrorDisplayComponent, RadioBtnComponent, TextInputComponent, ClientSmsResponseComponent, ClientSmsCustomKeywordsComponent, ClientStopHelpLanguagesComponent, ClientHelpsiteComponent, MessageDialogComponent_1]
})
export class ClientInputsNewComponent implements OnInit, OnDestroy {
  @ViewChild(MessageDialogComponent, { static: true })
  messageDialog: MessageDialogComponent;
  @Input() client: Client;

  shortCode: string;
  stopKeyword: SmsResponseLanguage;
  helpKeyword: SmsResponseLanguage;
  unrecognizedKeyword: SmsResponseLanguage;
  /** The Unrecognized response with only English, for the multi-language UI. */
  englishUnrecognizedKeyword: SmsResponseLanguage;
  /**
   * multi-language-stop-help is on for this client. Its STOP/HELP text then
   * lives in the language tables; the old fields are only a rollback copy.
   */
  multiLanguageStopHelp = false;
  /** The flag lookup failed or timed out, so today's boxes show with a notice. */
  multiLanguageStopHelpUnknown = false;
  canViewSmsLanguages = false;
  canEditSmsLanguages = false;
  englishStopHelpFallback: StopHelpTexts;
  /** Unsaved STOP/HELP edits, by language code; saved on Save. */
  stopHelpEdits: StopHelpEdits = {};
  /**
   * The one old Custom Notification Keyword checkbox that shows with the flag
   * on. The others' words are library keywords now (DIS-1810).
   */
  readonly stopAllOnly = [STOP_ALL_KEYWORD];
  maxLength: number;
  inputs: Array<Object>;

  headerGroups = {
    messageInputs: true,
    feUpload: true,
    roleMapping: true,
    clientHelpsite: true,
    asciiArmor: true,
    clientShortCodeLongCode: true,
  };

  featureFlags = FeatureFlags;
  dataInputsArray: UntypedFormArray;
  ssoRoleMapsArray: UntypedFormArray;
  shortCodeControl: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    CustomValidators.digitsOnly,
  ]);
  shortCodeGroup: UntypedFormGroup = new UntypedFormGroup({
    short_code: this.shortCodeControl,
  });

  constructor(
    private activatedRoute: ActivatedRoute,
    private clientService: ClientService,
    private titleService: TitleService,
    private router: Router,
    public featureService: FeatureService,
    public newFeed: NewFeedTransitionService,
    private smsLanguageConfigService: SmsLanguageConfigService,
    private permissionService: PermissionService,
  ) {
    this.client = new Client();
  }

  ngOnInit() {
    this.getClient();
  }

  ngOnDestroy() {
    this.titleService.deactivate();
  }

  addDataInput(dataInput: string = 'input_'): void {
    const dataInputValidator = Validators.pattern(/^(input_[a-zA-Z0-9-_.]*$)/);
    this.dataInputsArray.push(
      new UntypedFormControl(dataInput, [
        dataInputValidator,
        CustomValidators.isUnique(this.dataInputsArray),
      ]),
    );
  }

  removeDataInput(index): void {
    this.dataInputsArray.removeAt(index);
  }

  /**
   * SSO Role Maps Form
   */

  initSSORoleMapsForm(client: Client): void {
    this.ssoRoleMapsArray = new UntypedFormArray([]);
    _.each(client.sso_config.role_id_value_map, (roleMap) => {
      this.addSSORoleMap(roleMap);
    });
  }

  addSSORoleMap(
    roleMap = { client_role: 'client_role', relay_role: 'relay_role' },
  ): void {
    this.ssoRoleMapsArray.push(
      new UntypedFormGroup({
        client_role: new UntypedFormControl(roleMap.client_role),
        relay_role: new UntypedFormControl(roleMap.relay_role),
      }),
    );
  }

  removeSSORoleMap(index): void {
    this.ssoRoleMapsArray.removeAt(index);
  }

  /**
   * Form Utils + Validation
   */

  trackInput(index) {
    return index;
  }

  isInited() {
    return this.dataInputsArray && this.ssoRoleMapsArray;
  }

  isValid() {
    return (
      this.isInited() &&
      this.dataInputsArray.valid &&
      this.ssoRoleMapsArray.valid &&
      this.shortCodeGroup.valid
    );
  }

  /**
   * Return language options for Unrecognized response. Only support English response
   */
  get languageOptionsForUnrecognizedResponse(): string[] {
    return [LanguageEnum.ENGLISH];
  }

  /**
   * Saving
   */

  saveAndContinue(): void {
    this.updateModel();

    // Stop & Help text saves first, so a rejected save leaves everything
    // else (including the old fields it is copied into) unsaved too. Only
    // edited fields are sent; the server leaves the rest as they are.
    const savesSmsLanguages = this.savesSmsLanguages;
    const smsLanguagesSave = savesSmsLanguages
      ? this.smsLanguageConfigService.saveClientLanguages(
          this.client.id,
          Object.entries(this.stopHelpEdits).map(([languageCode, edit]) => ({
            languageCode,
            ...(edit.stop !== undefined && { stopResponseText: edit.stop }),
            ...(edit.help !== undefined && { helpResponseText: edit.help }),
          })),
        )
      : of(null);
    smsLanguagesSave.subscribe({
      next: () => this.saveClientAndShortCode(savesSmsLanguages),
      error: (err: HttpErrorResponse) =>
        this.messageDialog.showMessage(
          `Oops...could not save the Stop & Help responses: ${
            err.error?.reason ?? 'please try again.'
          }`,
        ),
    });
  }

  private saveClientAndShortCode(smsLanguagesSaved: boolean): void {
    forkJoin({
      clientSaveResult: this.clientService.updateClient(this.client),
      shortCodeResult: this.clientService.updateClientShortCode(
        this.client.id,
        this.shortCode,
      ),
    })
      .pipe(
        switchMap((mainResult) => {
          // only update the sms keywords if the shortCodeResult is successful.
          // With the flag on only the stopall checkbox shows, so only a
          // changed stopall is saved; the other keywords are left as they are.
          const stopAll = this.changedStopAll();
          if (mainResult.shortCodeResult && !this.multiLanguageStopHelp) {
            const keywords = this.getSmsKeywords(this.shortCodeGroup.value);
            return this.clientService
              .updateCustomSMSKeywords(this.client.id, this.shortCode, keywords)
              .pipe(
                map((keywordResult) => ({
                  clientShortCode: mainResult,
                  customKeywords: keywordResult,
                })),
              );
          } else if (mainResult.shortCodeResult && stopAll !== null) {
            return this.saveStopAll(stopAll).pipe(
              map((keywordResult) => ({
                clientShortCode: mainResult,
                customKeywords: keywordResult,
              })),
            );
          } else {
            // just return the clientShortCode combo result
            return of({ clientShortCode: mainResult, customKeywords: '' });
          }
        }),
      )
      .subscribe(
        (finalResult) => {
          this.onSuccessfulSave(finalResult.clientShortCode.clientSaveResult);
        },
        (err: HttpErrorResponse) => this.onFailedSaved(err, smsLanguagesSaved),
      );
  }

  /**
   * Other
   */

  previousStep(): void {
    this.router.navigateByUrl(`/client/${this.client.id}/edit/validation`);
  }

  toggleHeader(headerGroup: string) {
    if (this.headerGroups[headerGroup] !== undefined) {
      this.headerGroups[headerGroup] = !this.headerGroups[headerGroup];
    }
  }

  /**
   * Data Inputs Form
   */

  private initDataInputsForm(client: Client): void {
    this.dataInputsArray = new UntypedFormArray([]);
    _.each(client.ext_dynamic_fields, (dataInput) => {
      this.addDataInput(dataInput);
    });
  }

  private updateModel() {
    this.client.ext_dynamic_fields = this.dataInputsArray.getRawValue();
    this.client.sso_config.role_id_value_map =
      this.ssoRoleMapsArray.getRawValue();
    this.shortCode = this.shortCodeControl.value;
    if (this.savesSmsLanguages) {
      this.copyStopHelpToLegacyFields();
    }
  }

  private get savesSmsLanguages(): boolean {
    return (
      this.multiLanguageStopHelp &&
      this.canEditSmsLanguages &&
      Object.keys(this.stopHelpEdits).length > 0
    );
  }

  /**
   * During the soft launch, edited English, Spanish and French STOP/HELP
   * text is also written to the old client fields, so turning the flag off
   * (or a missing keyword document) sends the same text. Blank text leaves
   * the old field as it was.
   */
  private copyStopHelpToLegacyFields(): void {
    const { stop, help } = this.client.sms_keywords;
    for (const code of ['en', 'es', 'fr'] as const) {
      const edit = this.stopHelpEdits[code];
      if (edit?.stop?.trim()) {
        stop[code].response_template = edit.stop;
      }
      if (edit?.help?.trim()) {
        help[code].response_template = edit.help;
      }
    }
  }

  private onSuccessfulSave(res: Client): void {
    const clientId = res.id;
    this.router.navigateByUrl(`/client/${clientId}/edit/onboarding`);
  }

  private onFailedSaved(
    err: HttpErrorResponse,
    smsLanguagesSaved: boolean,
  ): void {
    const serverMessage = this.clientService.cleanseError(err);
    // The edits are kept, and saving them again is harmless, so Save again
    // finishes both.
    const smsLanguagesNote = smsLanguagesSaved
      ? ' The Stop & Help responses were saved. Save again to finish.'
      : '';
    this.messageDialog.showMessage(
      `Oops...could not save client: ${serverMessage}${smsLanguagesNote}`,
    );
  }

  private getClient(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const clientId = params['clientId'];
      forkJoin({
        client: this.clientService.getClient(clientId),
        smsShortCode: this.clientService
          .getClientShortCode(clientId)
          .pipe(catchError((error) => of({ shortcode: '', error: error }))), // can fail if no shortcode exists
        // Short timeout: with the flag off, this call mustn't hold up the page.
        // null: unknown, shown as today's boxes with a notice.
        multiLanguageStopHelp: this.smsLanguageConfigService
          .isEnabled(clientId)
          .pipe(
            timeout(5000),
            catchError(() => of(null)),
          ),
      })
        .pipe(take(1))
        .subscribe(
          ({ client, smsShortCode, multiLanguageStopHelp }) => {
            this.client = client;
            this.shortCode = smsShortCode.shortcode;
            this.stopKeyword = client.sms_keywords.stop;
            this.helpKeyword = client.sms_keywords.help;
            this.unrecognizedKeyword = client.sms_keywords.unrecognized;
            this.englishUnrecognizedKeyword = {
              en: client.sms_keywords.unrecognized.en,
            } as SmsResponseLanguage;
            this.stopHelpEdits = {};
            this.englishStopHelpFallback = {
              stop: client.sms_keywords.stop.en.response_template,
              help: client.sms_keywords.help.en.response_template,
            };
            this.multiLanguageStopHelp = multiLanguageStopHelp === true;
            this.multiLanguageStopHelpUnknown = multiLanguageStopHelp === null;
            this.canViewSmsLanguages = this.permissionService.checkPermissions(
              Permissions.sms_language_config_view,
            );
            this.canEditSmsLanguages = this.permissionService.checkPermissions(
              Permissions.sms_language_config_edit,
            );
            this.maxLength = client.sms_max_length;

            this.initDataInputsForm(client);
            this.initSSORoleMapsForm(client);
            this.shortCodeControl.patchValue(this.shortCode);
          },
          () => {
            this.messageDialog.showMessage(
              `Oops...Could not load client: ${params['clientId']}`,
            );
          },
          () => {
            this.setPrimaryTitle();
          },
        );
    });
  }

  private setPrimaryTitle(): void {
    this.titleService.activate(
      this.client && this.client.company_name
        ? 'Configure Client - ' + this.client.company_name
        : 'Configure Client',
    );
  }

  onMaxLengthChanged(event: Event) {
    // @ts-ignore
    this.maxLength = event.target.value;
  }

  onShortCodeChange(event: Event) {
    // @ts-ignore
    this.shortCode = event.target.value;
  }

  /**
   * Translates from the data held in FormControl back to an array of objects
   * to then translate to send to the service.
   * @param value The output of a .value call on the FormArray owned by the control.
   * @private
   */
  private getSmsKeywords(value: any): SmsCustomKeyword[] {
    return value.custom_sms_keywords
      .filter((keyword) => {
        return keyword.checked;
      })
      .map((keyword) => {
        const csk = keyword.custom_sms_keyword;
        return SmsCustomKeyword.deserialize(csk.keyword, csk);
      });
  }

  /**
   * With the flag on, the stopall checkbox's value if the user changed it,
   * else null (also when it never loaded).
   * @private
   */
  private changedStopAll(): boolean | null {
    const keywords = this.shortCodeGroup.get('custom_sms_keywords');
    if (!this.multiLanguageStopHelp || !(keywords instanceof UntypedFormArray)) {
      return null;
    }
    const checked = keywords.controls
      .find(
        (control) =>
          control.get('custom_sms_keyword.keyword')?.value === STOP_ALL_KEYWORD,
      )
      ?.get('checked');
    return checked?.dirty ? checked.value === true : null;
  }

  /**
   * Adds stopall to, or removes it from, the shortcode's current custom
   * keywords and saves them. The others are saved as they are: their
   * checkboxes are hidden, and another client can share the shortcode. A
   * shortcode with no keywords yet answers 404.
   * @param checked
   * @private
   */
  private saveStopAll(checked: boolean) {
    return this.clientService
      .getCustomSMSKeywords(this.client.id, this.shortCode)
      .pipe(
        catchError((err: HttpErrorResponse) =>
          err.status === 404
            ? of({ custom_sms_keywords: {} })
            : throwError(() => err),
        ),
        switchMap((current) => {
          const keywords = Object.entries(current?.custom_sms_keywords ?? {})
            .filter(([keyword]) => keyword !== STOP_ALL_KEYWORD)
            .map(([keyword, mapping]) =>
              SmsCustomKeyword.deserialize(keyword, mapping as object),
            );
          if (checked) {
            keywords.push(
              SmsCustomKeyword.deserialize(
                STOP_ALL_KEYWORD,
                smsCustomKeywordMasterList.custom_sms_keywords.stopall,
              ),
            );
          }
          return this.clientService.updateCustomSMSKeywords(
            this.client.id,
            this.shortCode,
            keywords,
          );
        }),
      );
  }
}
